import React from 'react';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import Text from '@root/components/v2/ui-elements/Text';
import moment from 'moment-timezone';

const DateFormatPipe = (parsedData, textOnly, seconds, formatType, alertsTimePatch, customTooltipText) => {
  if (!parsedData || parsedData === null) return '--';

  // Convert to string if it's not already
  const dataString = typeof parsedData === 'string' ? parsedData : String(parsedData);

  try {
    // Handle UTC timestamps (including those with microseconds)
    let parsedMoment;

    // Check if the timestamp ends with 'Z' or contains timezone info
    const isExplicitUTC = dataString.endsWith('Z') || dataString.includes('+00:00');

    // If no timezone indicator and looks like ISO format, assume UTC
    const isImplicitUTC = !isExplicitUTC && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dataString);

    if (isExplicitUTC || isImplicitUTC) {
      // Parse as UTC
      parsedMoment = moment.utc(dataString);
    } else {
      // Parse with timezone if present
      parsedMoment = moment.parseZone(dataString);
    }

    // Validate the parsed moment
    if (!parsedMoment.isValid()) {
      console.warn('Invalid date:', dataString);
      return '--';
    }

    // Get user's local timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localParsedTime = parsedMoment.tz(timezone);

    // Calculate UTC offset for display
    const offset = localParsedTime.utcOffset();
    const hours = Math.abs(Math.floor(offset / 60));
    const minutes = Math.abs(offset % 60);
    const utcOffset = `(UTC ${offset < 0 ? '-' : '+'}${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')})`;

    // Get the local timestamp
    const timestamp = localParsedTime.toDate();

    // Format based on parameters
    const getFormattedDate = () => {
      if (alertsTimePatch) {
        // This maintains UTC time but displays in local format
        return moment.utc(dataString).tz(timezone).format('YYYY-MM-DD hh:mm:ss A');
      }

      switch (formatType) {
        case 'date':
          return moment(timestamp).format('YYYY-MM-DD');
        default:
          if (seconds || textOnly) {
            return moment(timestamp).format('YYYY-MM-DD, hh:mm:ss A');
          }
          return moment(timestamp).format('YYYY-MM-DD, hh:mm A');
      }
    };

    const formattedDate = getFormattedDate();

    // Return text only if requested
    if (textOnly) {
      return formattedDate;
    }

    // Return with tooltip
    const tooltipTitle = customTooltipText || utcOffset;

    return (
      <Tooltip title={tooltipTitle} fromDate={true}>
        <span>
          <Text font="medium" fontWeight={'500'} element="span" style={{ lineHeight: 'normal' }}>
            {formattedDate}
          </Text>
        </span>
      </Tooltip>
    );
  } catch (error) {
    console.error('Error parsing date:', error, dataString);
    return '--';
  }
};

export default DateFormatPipe;

// Example usage with your timestamp format:
// DateFormatPipe('2025-06-04T19:00:23.447000') // Will parse as UTC and convert to local time
// DateFormatPipe('2025-06-04T19:00:23.447000Z') // Explicitly UTC
// DateFormatPipe('2025-06-04T19:00:23.447000+00:00') // Explicitly UTC
